import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Grid,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dashboardService from '../services/dashboardService';

const TopCustomersSection = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 12);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
  }, [startDate, endDate]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getTopCustomers(startDate, endDate);
      setCustomers(data);
    } catch (err) {
      console.error('Error loading top customers:', err);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, minHeight: 350 }}>
        <Typography variant="h6" gutterBottom>
          Top 5 Customers by Value
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, minHeight: 350 }}>
        <Typography variant="h6" gutterBottom>
          Top 5 Customers by Value
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, minHeight: 350 }}>
      <Typography variant="h6" gutterBottom>
        Top 5 Customers by Value
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        Click on any company name to view their details
      </Typography>

      {/* Date Range Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
      </Grid>

      {/* Customers Table */}
      <TableContainer sx={{ maxHeight: 300 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell align="right">Won Deals</TableCell>
              <TableCell align="right">Total Won Value</TableCell>
              <TableCell align="right">Pipeline Value</TableCell>
              <TableCell align="right">Last Won Deal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.length > 0 ? (
              customers.map((customer) => (
                <TableRow key={customer.CompanyId} hover>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate(`/companies/${customer.CompanyId}`)}
                      sx={{ textDecoration: 'none' }}
                    >
                      {customer.CompanyName}
                    </Link>
                  </TableCell>
                  <TableCell align="right">
                    {customer.WonDeals} / {customer.TotalDeals}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(customer.TotalWonValue)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(customer.TotalPipelineValue)}
                  </TableCell>
                  <TableCell align="right">
                    {formatDate(customer.LastActivityDate)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    No customer data available for the selected date range
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TopCustomersSection; 